import { queryClient } from '@/shared/api'
import type {
  CatalogAccess,
  CatalogCollectionDetailResponse,
  CatalogCollectionVideoItem,
  CatalogListItem,
  CatalogResponse,
  CatalogSearchItem,
  CatalogVideoDetailResponse,
} from '../api/catalog-api'

type CatalogCacheData =
  | CatalogResponse
  | CatalogCollectionDetailResponse
  | CatalogVideoDetailResponse

type BookmarkTarget = {
  itemKind: 'collection' | 'video'
  itemId: string
  bookmarked: boolean
}

export type BookmarkCacheSeedItem = {
  kind: 'collection' | 'video'
  id: string
  title: string
  urlSlug: string
  coverUrl: string | null
  shortDescription: string | null
  totalVideos: number | null
  totalDurationSeconds: number | null
  durationSeconds: number | null
  fileSizeBytes: number | null
  access: CatalogAccess
  openUrl: string | null
  isBookmarked: boolean
  categories?: string[]
}

export const isCatalogListQueryKey = (queryKey: readonly unknown[]) =>
  queryKey.length === 2 && queryKey[0] === 'catalog' && typeof queryKey[1] === 'string'

const isCatalogQueryKey = (queryKey: readonly unknown[]) =>
  queryKey.length > 0 && queryKey[0] === 'catalog'

const isCatalogResponse = (data: unknown): data is CatalogResponse => {
  if (!data || typeof data !== 'object') {
    return false
  }

  return 'mode' in data && 'query' in data
}

const isCatalogCollectionDetailResponse = (
  data: unknown
): data is CatalogCollectionDetailResponse => {
  if (!data || typeof data !== 'object') {
    return false
  }

  return 'collection' in data && 'videos' in data
}

const isCatalogVideoDetailResponse = (data: unknown): data is CatalogVideoDetailResponse => {
  if (!data || typeof data !== 'object') {
    return false
  }

  return 'video' in data && 'resources' in data
}

const updateListBookmark = (item: CatalogListItem, target: BookmarkTarget) => {
  if (
    item.kind !== target.itemKind ||
    item.id !== target.itemId ||
    item.isBookmarked === target.bookmarked
  ) {
    return item
  }

  return {
    ...item,
    isBookmarked: target.bookmarked,
  }
}

const updateSearchBookmark = (item: CatalogSearchItem, target: BookmarkTarget) => {
  if (
    item.kind !== target.itemKind ||
    item.id !== target.itemId ||
    item.isBookmarked === target.bookmarked
  ) {
    return item
  }

  return {
    ...item,
    isBookmarked: target.bookmarked,
  }
}

const updateCollectionVideoBookmark = (
  item: CatalogCollectionVideoItem,
  target: BookmarkTarget
) => {
  if (
    target.itemKind !== 'video' ||
    item.id !== target.itemId ||
    item.isBookmarked === target.bookmarked
  ) {
    return item
  }

  return {
    ...item,
    isBookmarked: target.bookmarked,
  }
}

const toSearchItemFromSeed = (item: BookmarkCacheSeedItem): CatalogSearchItem => ({
  kind: item.kind,
  id: item.id,
  title: item.title,
  urlSlug: item.urlSlug,
  coverUrl: item.coverUrl,
  shortDescription: item.shortDescription,
  totalVideos: item.totalVideos,
  totalDurationSeconds: item.totalDurationSeconds,
  durationSeconds: item.durationSeconds,
  fileSizeBytes: item.fileSizeBytes,
  isBookmarked: item.isBookmarked,
  access: item.access,
  openUrl: item.openUrl,
  categories: item.categories ?? [],
})

const patchCatalogResponse = (
  data: CatalogResponse,
  target: BookmarkTarget,
  seedItem?: BookmarkCacheSeedItem
): CatalogResponse => {
  if (data.mode === 'browse' && data.browse) {
    let changed = false

    const rows = data.browse.rows.map((row) => {
      const items = row.items.map((item) => {
        const updated = updateListBookmark(item, target)
        if (updated !== item) {
          changed = true
        }
        return updated
      })

      if (items === row.items) {
        return row
      }

      return {
        ...row,
        items,
      }
    })

    if (!changed) {
      return data
    }

    return {
      ...data,
      browse: {
        ...data.browse,
        rows,
      },
    }
  }

  if (data.mode === 'search' && data.searchResults) {
    const favoritesOnly = Boolean(data.query.favorites)
    const originalItems = data.searchResults.items
    let items = originalItems.map((item) => updateSearchBookmark(item, target))

    if (favoritesOnly) {
      if (!target.bookmarked) {
        items = items.filter(
          (item) => !(item.kind === target.itemKind && item.id === target.itemId)
        )
      } else if (
        seedItem &&
        seedItem.kind === target.itemKind &&
        seedItem.id === target.itemId &&
        !items.some((item) => item.kind === target.itemKind && item.id === target.itemId)
      ) {
        items = [{ ...toSearchItemFromSeed(seedItem), isBookmarked: true }, ...items]
      }
    }

    if (items === originalItems) {
      return data
    }

    return {
      ...data,
      searchResults: {
        ...data.searchResults,
        total: favoritesOnly ? items.length : data.searchResults.total,
        items,
      },
    }
  }

  return data
}

const patchCatalogCollectionDetailResponse = (
  data: CatalogCollectionDetailResponse,
  target: BookmarkTarget
): CatalogCollectionDetailResponse => {
  let changed = false

  const collection =
    target.itemKind === 'collection' &&
    data.collection.id === target.itemId &&
    data.collection.isBookmarked !== target.bookmarked
      ? {
          ...data.collection,
          isBookmarked: target.bookmarked,
        }
      : data.collection

  if (collection !== data.collection) {
    changed = true
  }

  const videos = data.videos.map((item) => {
    const updated = updateCollectionVideoBookmark(item, target)
    if (updated !== item) {
      changed = true
    }
    return updated
  })

  const selectedVideo = data.selectedVideo
    ? target.itemKind === 'video' &&
      data.selectedVideo.id === target.itemId &&
      data.selectedVideo.isBookmarked !== target.bookmarked
      ? {
          ...data.selectedVideo,
          isBookmarked: target.bookmarked,
        }
      : data.selectedVideo
    : data.selectedVideo

  if (selectedVideo !== data.selectedVideo) {
    changed = true
  }

  if (!changed) {
    return data
  }

  return {
    ...data,
    collection,
    videos,
    selectedVideo,
  }
}

const patchCatalogVideoDetailResponse = (
  data: CatalogVideoDetailResponse,
  target: BookmarkTarget
): CatalogVideoDetailResponse => {
  if (
    target.itemKind !== 'video' ||
    data.video.id !== target.itemId ||
    data.video.isBookmarked === target.bookmarked
  ) {
    return data
  }

  return {
    ...data,
    video: {
      ...data.video,
      isBookmarked: target.bookmarked,
    },
  }
}

const patchCatalogCacheData = (
  data: CatalogCacheData,
  target: BookmarkTarget,
  seedItem?: BookmarkCacheSeedItem
): CatalogCacheData => {
  if (isCatalogResponse(data)) {
    return patchCatalogResponse(data, target, seedItem)
  }

  if (isCatalogCollectionDetailResponse(data)) {
    return patchCatalogCollectionDetailResponse(data, target)
  }

  if (isCatalogVideoDetailResponse(data)) {
    return patchCatalogVideoDetailResponse(data, target)
  }

  return data
}

export type CatalogCacheSnapshots = ReturnType<typeof captureCatalogCacheSnapshots>

export const captureCatalogCacheSnapshots = () =>
  queryClient.getQueriesData<unknown>({
    predicate: (query) => isCatalogQueryKey(query.queryKey),
  })

export const restoreCatalogCacheSnapshots = (snapshots: CatalogCacheSnapshots) => {
  for (const [queryKey, data] of snapshots) {
    queryClient.setQueryData(queryKey, data)
  }
}

export const applyBookmarkToCatalogCache = (
  input: BookmarkTarget & { seedItem?: BookmarkCacheSeedItem }
) => {
  queryClient.setQueriesData<unknown>(
    {
      predicate: (query) => isCatalogQueryKey(query.queryKey),
    },
    (current: unknown) => {
      if (!current || typeof current !== 'object') {
        return current
      }

      return patchCatalogCacheData(current as CatalogCacheData, input, input.seedItem)
    }
  )
}
